/* eslint-disable react-refresh/only-export-components */
import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'Service' | 'FAQPage';
  data: Record<string, unknown>;
}

export function SchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': type,
      ...data,
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [type, data]);

  return null;
}

export function getOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    name: 'CodeLiance',
    url: siteUrl,
    logo: `${siteUrl}/logo.png`,
    description: 'CodeLiance ofrece desarrollo de software personalizado, infraestructura en la nube e inteligencia artificial para transformar tu negocio.',
    foundingDate: '2008',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+1-555-0123',
      contactType: 'customer service',
      availableLanguage: ['Spanish', 'English'],
    },
    sameAs: [
      'https://www.facebook.com/codeliance',
      'https://www.twitter.com/codeliance',
      'https://www.linkedin.com/company/codeliance',
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'ES',
    },
  };
}

export function getWebSiteSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    name: 'CodeLiance',
    url: siteUrl,
    description: 'Soluciones Tecnológicas Innovadoras - Desarrollo de Software & IA',
    publisher: {
      '@type': 'Organization',
      name: 'CodeLiance',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${siteUrl}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

export function getServiceSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    '@type': 'Service',
    serviceType: 'Software Development',
    provider: {
      '@type': 'Organization',
      name: 'CodeLiance',
      url: siteUrl,
    },
    areaServed: 'Worldwide',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Servicios de Desarrollo de Software',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Desarrollo de Software Personalizado',
            description: 'Soluciones de software a medida para tu negocio',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Infraestructura en la Nube',
            description: 'Servicios de cloud computing y DevOps',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Inteligencia Artificial',
            description: 'Soluciones de IA y machine learning',
          },
        },
      ],
    },
  };
}

export function getFAQSchema() {
  return {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '¿Cuánto cuesta desarrollar software a medida?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'El costo de desarrollo de software personalizado depende de la complejidad del proyecto, funcionalidades requeridas y plataforma. Contáctanos para una cotización personalizada.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Cuánto tiempo toma desarrollar una aplicación móvil?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'El tiempo de desarrollo varía según los requisitos. Una app básica puede tomar 2-3 meses, mientras que soluciones enterprise pueden requerir 6-12 meses.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Ofrecen soporte después del lanzamiento?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, oferecemos soporte técnico 24/7, mantenimiento continuo y actualizaciones para garantizar el óptimo funcionamiento de tu software.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Qué tecnologías utilizan para el desarrollo?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Trabajamos con tecnologías modernas incluyendo React, Node.js, Python, AWS, Azure, y soluciones de IA como ChatGPT y modelos personalizados de machine learning.',
        },
      },
      {
        '@type': 'Question',
        name: '¿Pueden migrar mi sistema actual a la nube?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Sí, ofrecemos servicios de migración a la nube con AWS y Azure, garantizando cero tiempo de inactividad y optimización de costos.',
        },
      },
    ],
  };
}

export function getProductSchema(productName: string, description: string, url: string) {
  return {
    '@type': 'SoftwareApplication',
    name: productName,
    description: description,
    url: url,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, iOS, Android',
    provider: {
      '@type': 'Organization',
      name: 'CodeLiance',
      url: 'https://codeliance.co',
    },
  };
}
